import React from "react";
import { Grid, useTheme } from "@mui/material";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { GridColumns } from "@mui/x-data-grid";
import { intlFormatDateShort } from "../../../utils/time-utils";
import { CustomDate } from "../../../models/custom-date/custom-date";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import {
  EMPLOYEE_COST_SECTION_TYPES,
  SELECTED_PERIOD,
  SELECTED_PERIOD_DIVIDER,
} from "../../../utils/constants/employee-cost-report-constants";
import Table from "../../../reusable-components/Table/Table";
import { ProformaEmployeeCostReportApiService } from "../../../services/proforma-employee-cost-report/proforma-employee-cost-report-api.service";
import AddEditProformaEmployeeCostReport from "../../../containers/AddEditProformaEmployeeCostReport/AddEditProformaEmployeeCostReport";
import MarkAsAcceptedByClient from "../../../containers/AddEditProformaEmployeeCostReport/MarkAsAcceptedByClient";
import {
  LegalEntityQuickfilter,
  useLegalEntityQuickFilter,
} from "../../../containers/Quickfilters/LegalEntityQuickfilter";
import QuickfiltersContainer from "../../../containers/Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { ProformaEmployeeCostReportFilter } from "../../../models/proforma-employee-cost-report/proforma-employee-cost-report-filter";
import { FT_PECR } from "../../../utils/constants/filter-tables-constants";
import SentProformaEmployeeCostReport from "../../../containers/AddEditProformaEmployeeCostReport/SentProformaEmployeeCostReport/SentProformaEmployeeCostReport";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import { isAuthorizedLE } from "../../../custom-hooks/useAuthorization/useAuthorization";
const ProformaEmployeeCostReportTable = () => {
  const tRP = "pages.employee_cost_report_page.tables.proforma_employee_cost_report";
  const { t } = useTranslation();
  const theme = useTheme();

  const ACL = [
    AUTHORIZATIONS.LegalEntityAccess.ProcessPayroll,
    AUTHORIZATIONS.LegalEntityAccess.ProcessAccounting,
    AUTHORIZATIONS.LegalEntityAccess.RunAccountingReports,
  ];
  const {
    legalEntitiesQuickFilter,
    setLegalEntitiesQuickFilter,
    selectedLegalEntity,
    allLegalEntities,
    legalEntityQFIsLoading,
  } = useLegalEntityQuickFilter(true, undefined, [...ACL], isAuthorizedLE);

  const columns: GridColumns = [
    {
      field: "legalEntity",
      headerAlign: "center",
      flex: 1,
      renderHeader: () => <Typography>{`${t(`common_words.legal_entity`)}`}</Typography>,
      renderCell: ({ row }) => <Typography>{row.legalEntity?.code ?? row.legalEntity?.name}</Typography>,
    },
    {
      field: "description",
      headerAlign: "center",
      align: "center",
      flex: 1.5,
      headerName: `${t(`${tRP}.fields.description`)}`,
      renderCell: ({ row }) => <Typography>{row.description}</Typography>,
    },
    // {
    //   field: "project",
    //   headerAlign: "center",
    //   flex: 1,
    //   headerName: `${t(`common_words.project`)}`,
    // },
    // {
    //   field: "employee",
    //   headerAlign: "center",
    //   flex: 1,
    //   headerName: `${t(`common_words.employee`)}`,
    // },
    {
      field: "applicableFrom",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerName: `${t(`${tRP}.fields.valid_as_of`)}`,
      renderCell: ({ row }) => (
        <Typography>
          {!!CustomDate.ToDate(row.applicableFrom) ? intlFormatDateShort(CustomDate.ToDate(row.applicableFrom)!) : ""}
        </Typography>
      ),
    },
    // {
    //   field: "applicableTo",
    //   headerAlign: "center",
    //   align: "center",
    //   flex: 1,
    //   headerName: `${t(`${tRP}.fields.applicable_to`)}`,
    //   renderCell: ({ row }) => (
    //     <Typography>
    //       {row.applicableTo && !!CustomDate.ToDate(row.applicableTo)
    //         ? intlFormatDateShort(CustomDate.ToDate(row.applicableTo)!)
    //         : ""}
    //     </Typography>
    //   ),
    // },
    {
      field: "",
      headerAlign: "center",
      flex: 1.5,
      renderHeader: () => <Typography>{`${t(`${tRP}.fields.price_to_the_client`)}`}</Typography>,
      renderCell: ({ row }) => {
        const amount = row.sections.find(
          (section) => section.type?.type?.id === EMPLOYEE_COST_SECTION_TYPES.Price_to_the_client.id
        )?.amount;

        let calculatedAmount: number = amount;

        switch (row.selectedPeriod) {
          case SELECTED_PERIOD.yearly:
            break;
          case SELECTED_PERIOD.monthly:
            calculatedAmount = amount / SELECTED_PERIOD_DIVIDER.monthly;
            break;
          case SELECTED_PERIOD.daily:
            calculatedAmount = amount / SELECTED_PERIOD_DIVIDER.daily;
            break;
          case SELECTED_PERIOD.hourly:
            calculatedAmount = amount / SELECTED_PERIOD_DIVIDER.hourly;
            break;
        }

        return (
          <Grid style={{ display: "flex", justifyContent: "space-between", width: "100%", gap: "5px" }}>
            <Typography>
              <b>
                {row.selectedPeriod === SELECTED_PERIOD.yearly
                  ? `${t(`${tRP}.fields.yearly`)}`
                  : row.selectedPeriod === SELECTED_PERIOD.monthly
                  ? `${t(`${tRP}.fields.monthly`)}`
                  : row.selectedPeriod === SELECTED_PERIOD.daily
                  ? `${t(`${tRP}.fields.daily`)}`
                  : row.selectedPeriod === SELECTED_PERIOD.hourly
                  ? `${t(`${tRP}.fields.hourly`)}`
                  : ""}
              </b>
            </Typography>
            <Typography>{`${calculatedAmount?.toLocaleCustom()} ${row.currency?.code ?? ""}`}</Typography>
          </Grid>
        );
      },
    },
    {
      field: "sentToClient",
      align: "center",
      headerAlign: "center",
      flex: 1,
      renderHeader: () => <Typography>{`${t(`${tRP}.fields.is_sent_to_client`)}`}</Typography>,
      renderCell: ({ row }) => (
        <Typography>
          {row.sentToClient ? <SentProformaEmployeeCostReport proformaEmployeeCostReportId={row.id} /> : <></>}
        </Typography>
      ),
    },
    {
      field: "isAcceptedByClient",
      align: "center",
      headerAlign: "center",
      flex: 1,
      renderHeader: () => <Typography>{`${t(`${tRP}.fields.is_accepted_by_client`)}`}</Typography>,
      renderCell: ({ row }) => (
        <Typography>
          {row.isAcceptedByClient ? (
            <FontAwesomeIcon
              icon={faCircleCheck}
              color={theme.palette.success.main}
            />
          ) : (
            <></>
          )}
        </Typography>
      ),
    },
  ];
  return (
    <>
      {!legalEntityQFIsLoading && (
        <Table
          translationRootPath={tRP}
          filter={FT_PECR}
          serviceApi={new ProformaEmployeeCostReportApiService()}
          quickFilters={
            <QuickfiltersContainer>
              <LegalEntityQuickfilter
                quickFilters={legalEntitiesQuickFilter}
                handleChangeQuickFilters={setLegalEntitiesQuickFilter}
                allLegalEntitiesOption
                allLegalEntities={allLegalEntities}
                authorizations={ACL}
                optionIsAuthorizedChecker={isAuthorizedLE}
              />
            </QuickfiltersContainer>
          }
          propsFilter={
            {
              legalEntityIds: legalEntitiesQuickFilter.legalEntities.legalEntityIds,
            } as ProformaEmployeeCostReportFilter
          }
          columns={columns}
          CustomAddEditComponent={AddEditProformaEmployeeCostReport}
          propsForCustomAddEdit={{
            legalEntity: selectedLegalEntity,
          }}
          addEditType="custom"
          AdditionalActionsforAddEditColumn={MarkAsAcceptedByClient}
          displayTitle={false}
          checkIsRowDeletable={(row) => !row.isAcceptedByClient && !row.sentToClient}
        />
      )}
    </>
  );
};
export default ProformaEmployeeCostReportTable;
